import { NavigateNext } from '@mui/icons-material'
import MenuItem from '@mui/material/MenuItem'
import Box from '@mui/material/Box'
import { Components } from '@mui/material/styles'
import {
  DataGridProps,
  GridCsvExportMenuItem,
  GridSlotProps,
  GridSlotsComponent,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  GridToolbarProps,
  GridToolbarQuickFilter,
  ToolbarPropsOverrides,
  useGridApiContext
} from '@mui/x-data-grid'
import * as XLSX from 'xlsx'
import { useTranslation } from 'react-i18next'
import { JSXElementConstructor, ReactNode } from 'react'
import { Stack } from '@mui/material'

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    beforeQuickFilter?: ReactNode
    afterQuickFilter?: ReactNode
  }
}

function CustomExport() {
  const apiRef = useGridApiContext()
  const { t } = useTranslation('common')

  function handleXlsxExport() {
    const columns = apiRef.current
      .getAllColumns()
      .filter((col) => col.field !== '__check__')
      .map((col) => ({
        field: col.field,
        header: col.headerName || col.field
      }))

    const rows = apiRef.current.getRowModels()

    const data = Array.from(rows.keys()).map((id) =>
      columns.map(({ field }) => {
        const params = apiRef.current.getCellParams(id, field)
        return params.value
      })
    )

    data.unshift(columns.map((col) => col.header))

    const worksheet = XLSX.utils.aoa_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    const path = document.title.replace(/\//g, '_').replace(/^_+|_+$/g, '')
    const filename = path ? `${path}.xlsx` : 'data.xlsx'

    XLSX.writeFile(workbook, filename)
  }

  return (
    <GridToolbarExportContainer>
      <GridCsvExportMenuItem options={{}} />
      <MenuItem onClick={handleXlsxExport}>{t('downloadAsXLSX')}</MenuItem>
    </GridToolbarExportContainer>
  )
}

type CustomToolbarProps = GridToolbarProps & {
  beforeQuickFilter?: ReactNode
  afterQuickFilter?: ReactNode
}

export function CustomToolbar({ beforeQuickFilter, afterQuickFilter }: CustomToolbarProps) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <CustomExport />

      <Box sx={{ flex: 1 }} />

      <Stack direction="row" alignItems="center" gap={2}>
        {beforeQuickFilter}
        <GridToolbarQuickFilter />
        {afterQuickFilter}
      </Stack>
    </GridToolbarContainer>
  )
}

const components: Components = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true
    },
    styleOverrides: {
      root: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      }
    }
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover'
    }
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: {
        variant: 'h6'
      },
      subheaderTypographyProps: {
        marginTop: 1
      }
    },
    styleOverrides: {
      action: {
        marginTop: '-4px',
        marginRight: '-4px'
      }
    }
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px -1px',
        backgroundImage: 'none'
      }
    }
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        boxShadow: 'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px -1px',
        backgroundImage: 'none'
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none'
      }
    }
  },
  MuiPickersDay: {
    styleOverrides: {
      day: {
        fontWeight: '300'
      }
    }
  },
  MuiPickersYear: {
    styleOverrides: {
      root: {
        height: '64px'
      }
    }
  },
  MuiPickersCalendar: {
    styleOverrides: {
      transitionContainer: {
        marginTop: '6px'
      }
    }
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      iconButton: {
        backgroundColor: 'transparent',
        '& > *': {
          backgroundColor: 'transparent'
        }
      },
      switchHeader: {
        marginTop: '2px',
        marginBottom: '4px'
      }
    }
  },
  MuiPickersClock: {
    styleOverrides: {
      container: {
        margin: `32px 0 4px`
      }
    }
  },
  MuiPickersClockNumber: {
    styleOverrides: {
      clockNumber: {
        left: `calc(50% - 16px)`,
        width: '32px',
        height: '32px'
      }
    }
  },
  MuiPickerDTHeader: {
    styleOverrides: {
      dateHeader: {
        '& h4': {
          fontSize: '2.125rem',
          fontWeight: 400
        }
      },
      timeHeader: {
        '& h3': {
          fontSize: '3rem',
          fontWeight: 400
        }
      }
    }
  },
  MuiPickersTimePicker: {
    styleOverrides: {
      hourMinuteLabel: {
        '& h2': {
          fontSize: '3.75rem',
          fontWeight: 300
        }
      }
    }
  },
  MuiPickersToolbar: {
    styleOverrides: {
      toolbar: {
        '& h4': {
          fontSize: '2.125rem',
          fontWeight: 400
        }
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '6px'
      }
    }
  },
  MuiListItemText: {
    styleOverrides: {
      root: {
        '&.MuiListItemText-multiline': {
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          textOverflow: 'ellipsis',
          textWrap: 'nowrap',
          overflow: 'hidden',
          paddingRight: '48px'
        },
        '& .MuiListItemText-primary': {
          order: 2
        },
        '& .MuiListItemText-secondary': {
          order: 1
        }
      }
    }
  },
  MuiListItemSecondaryAction: {
    styleOverrides: {
      root: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    }
  },
  MuiBreadcrumbs: {
    defaultProps: {
      maxItems: 3,
      itemsBeforeCollapse: 0,
      itemsAfterCollapse: 3,
      separator: <NavigateNext />
    },
    styleOverrides: {
      ol: {
        flexWrap: 'nowrap',
        textOverflow: 'ellipsis',
        textWrap: 'nowrap',
        overflowX: 'auto',
        width: '100%'
      },
      li: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        maxWidth: '250px',
        '> p': {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden'
        }
      }
    }
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        position: 'relative',
        '> .MuiTabScrollButton-root:last-child': {
          right: 0,
          left: 'auto'
        }
      }
    }
  },
  MuiTabScrollButton: {
    styleOverrides: {
      root: {
        position: 'absolute',
        height: '100%'
      }
    }
  },
  MuiTabsScroller: {
    styleOverrides: {
      '+ MuiTabScrollButton-root': {
        right: 0,
        left: 'auto'
      }
    }
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingBottom: '16px'
      }
    }
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingBottom: '16px'
      }
    }
  },
  MuiDataGrid: {
    defaultProps: {
      slots: {
        toolbar: CustomToolbar
      },
      slotProps: {
        toolbar: {
          csvOptions: { disableToolbarButton: false },
          printOptions: { disableToolbarButton: true },
          showQuickFilter: true,
          quickFilterProps: {
            variant: 'outlined',
            size: 'small',
            debounceMs: 250
          }
        }
      }
    },
    styleOverrides: {
      root: {
        '--DataGrid-containerBackground': 'transparent',
        '& .MuiDataGrid-toolbarContainer': {
          marginBottom: '12px'
        },
        border: 0
      }
    }
  }
}

export default components
